// Guide 19.9, "turn daily notes into status updates": a foreman's note is
// read against this view's rows and statuses are PROPOSED, each with the words
// of the note it rests on. Nothing changes until a person ticks the ones to
// apply; those go through the ordinary status route, with the quote as the
// update's note. Backend: POST /views/:id/tracking/from-note.
import React, { useState } from "react";
import { Button, Checkbox, Input, Modal, message } from "antd";
import { viewsApi } from "./viewModel";

const STATUS_LABEL = { projected: "Projected", procured: "Procured", being_installed: "Being installed", installed: "Installed" };

export default function DailyNote({ projectId, view, onApplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [picked, setPicked] = useState(new Set());

  const read = async () => {
    setBusy(true);
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/tracking/from-note`, { method: "POST", body: { text } });
      setProposals(out.proposals || []);
      setPicked(new Set((out.proposals || []).map((_, i) => i)));
      if (!(out.proposals || []).length) message.info("The note does not clearly cover any element on this view");
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    let n = 0;
    try {
      for (const [i, p] of proposals.entries()) {
        if (!picked.has(i)) continue;
        // eslint-disable-next-line no-await-in-loop
        const out = await viewsApi(projectId, `/${view.viewId}/tracking/status`, { method: "POST", body: { rowIds: p.rowIds, status: p.status, note: `Daily note: "${p.quote}"` } });
        n += out.written || 0;
      }
      message.success(`${n} status${n === 1 ? "" : "es"} written from the note`);
      setOpen(false); setProposals(null); setText("");
      onApplied();
    } catch (e) { message.error(`${n} written, then: ${e.message}`); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>Read a daily note</Button>
      <Modal open={open} width={680} title="Statuses from a daily note" onCancel={() => { setOpen(false); setProposals(null); }}
             okText={proposals ? `Apply ${picked.size}` : "Read the note"} okButtonProps={{ disabled: proposals ? !picked.size : !text.trim() }}
             confirmLoading={busy} onOk={proposals ? apply : read}>
        {!proposals ? (
          <>
            <p className="g-tsub">Paste what the field wrote. The elements it clearly covers are proposed with a status and the words they rest on; nothing changes until you apply them.</p>
            <Input.TextArea id="daily-note" rows={6} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} aria-label="The daily note"
                            placeholder="e.g. Installed the 2 in sleeves at IDF 214 between grids 10 and 8. Conduit to MDF 129 roughed in, not pulled." />
          </>
        ) : (
          proposals.map((p, i) => (
            <div key={i} style={{ margin: "6px 0" }}>
              <Checkbox checked={picked.has(i)} onChange={(e) => { const next = new Set(picked); if (e.target.checked) next.add(i); else next.delete(i); setPicked(next); }}>
                <b>{STATUS_LABEL[p.status] || p.status}</b>: {p.rowIds.join(", ")}
                {p.quote && <div className="g-tsub">"{p.quote}"</div>}
              </Checkbox>
            </div>
          ))
        )}
      </Modal>
    </>
  );
}
